import React, { useEffect, useState } from "react";

// To get time formatted in given timezone
const getTimeParts = (timezone) => {
  const now = new Date();
  const options = {
    timeZone: timezone,
    hour12: true,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  };
  const timeString = now.toLocaleTimeString("en-US", options);
  const [hourStr, minuteStr, secondStr, ampm] = timeString
    .replace(",", "")
    .split(/:| /)
    .filter(Boolean);
  return {
    hour: Number(hourStr),
    minute: Number(minuteStr),
    second: Number(secondStr),
    ampm,
    formattedTime: timeString,
  };
};

// To render clock numbers 1 to 12
const ClockNumbers = () => {
  const numbers = [];
  for (let i = 1; i <= 12; i++) {
    const angle = (i * 30 - 90) * (Math.PI / 180);
    const radius = 60;
    const x = 75 + radius * Math.cos(angle);
    const y = 75 + radius * Math.sin(angle);
    numbers.push(
      <text
        key={i}
        x={x}
        y={y + 5}
        textAnchor="middle"
        fontSize="16"
        fontWeight="600"
        fill="#444"
        fontFamily="Segoe UI, Tahoma, Geneva, Verdana, sans-serif"
        userSelect="none"
      >
        {i}
      </text>,
    );
  }
  return <>{numbers}</>;
};

// To render tick marks for each hour
const ClockTicks = () => {
  const ticks = [];
  for (let i = 0; i < 12; i++) {
    const angle = (i * 30 - 90) * (Math.PI / 180);
    const innerRadius = 68;
    const outerRadius = 75;
    const x1 = 75 + innerRadius * Math.cos(angle);
    const y1 = 75 + innerRadius * Math.sin(angle);
    const x2 = 75 + outerRadius * Math.cos(angle);
    const y2 = 75 + outerRadius * Math.sin(angle);
    ticks.push(
      <line
        key={i}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="#999"
        strokeWidth="2"
      />,
    );
  }
  return <>{ticks}</>;
};

const AnalogClock = ({
  timezone,
  label,
  width = 150,
  height = 150,
  containerHeight = 280,
}) => {
  const [time, setTime] = useState(getTimeParts(timezone));

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(getTimeParts(timezone));
    }, 1000);
    return () => clearInterval(interval);
  }, [timezone]);

  // Calculate rotation degrees for each hand
  const hourDeg = ((time.hour % 12) + time.minute / 60) * 30;
  const minuteDeg = time.minute * 6;
  const secondDeg = time.second * 6;

  return (
    <div
      className="clock-container"
      style={{
        width: width * (200 / 150), // scale container width proportionally (default was 200px)
        padding: "1.5rem 2rem",
        fontSize: "16px",
        height: containerHeight,
      }}
    >
      <svg
        className="clock"
        viewBox="0 0 150 150"
        width={width}
        height={height}
        aria-label={`Analog clock for ${label}`}
      >
        <circle cx="75" cy="75" r="74" />
        <ClockTicks />
        <ClockNumbers />
        {/* Hour hand */}
        <line
          className="hour-hand"
          x1="75"
          y1="75"
          x2={75 + 30 * Math.cos(Math.PI / 2 - (hourDeg * Math.PI) / 180)}
          y2={75 - 30 * Math.sin(Math.PI / 2 - (hourDeg * Math.PI) / 180)}
        />
        {/* Minute hand */}
        <line
          className="minute-hand"
          x1="75"
          y1="75"
          x2={75 + 45 * Math.cos(Math.PI / 2 - (minuteDeg * Math.PI) / 180)}
          y2={75 - 45 * Math.sin(Math.PI / 2 - (minuteDeg * Math.PI) / 180)}
        />
        {/* Second hand */}
        <line
          className="second-hand"
          x1="75"
          y1="75"
          x2={75 + 55 * Math.cos(Math.PI / 2 - (secondDeg * Math.PI) / 180)}
          y2={75 - 55 * Math.sin(Math.PI / 2 - (secondDeg * Math.PI) / 180)}
        />
        {/* Center circle */}
        <circle className="center-circle" cx="75" cy="75" r="5" />
      </svg>
      <div className="timezone-label">{label}</div>
      <div className="time-label">{time.formattedTime}</div>
      <div className="date-label">
        {new Date().toLocaleDateString("en-US", {
          timeZone: timezone,
          month: "short",
          day: "numeric",
          year: "numeric",
        })}
      </div>

      <style>{`
        .clock-container {
          background: #ffffff;
          border-radius: 20px;
          box-shadow:
            0 8px 20px rgba(0,0,0,0.1),
            0 2px 6px rgba(0,0,0,0.06);
          padding: 1.5rem 2rem;
          width: 200px;
          text-align: center;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          user-select: none;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          cursor: default;
        }
        .clock-container:hover {
          transform: translateY(-6px);
          box-shadow:
            0 12px 30px rgba(0,0,0,0.15),
            0 4px 10px rgba(0,0,0,0.1);
        }
        .clock {
          filter: drop-shadow(0 3px 6px rgba(0,0,0,0.08));
          user-select: none;
          margin-bottom: 1rem;
        }
        circle {
          fill: #fafafa;
          stroke: #888;
          stroke-width: 2.5;
          transition: fill 0.3s ease, stroke 0.3s ease;
        }
        line {
          transition: stroke-width 0.3s ease;
        }
        line.hour-hand {
          stroke: #222 !important;
          stroke-width: 6 !important;
          stroke-linecap: round !important;
        }
        line.minute-hand {
          stroke: #555 !important;
          stroke-width: 4 !important;
          stroke-linecap: round !important;
        }
        line.second-hand {
          stroke: #d32f2f !important;
          stroke-width: 2 !important;
          stroke-linecap: round !important;
        }
        circle.center-circle {
          fill: #444 !important;
          transition: fill 0.3s ease;
        }
        text {
          fill: #444;
          font-weight: 600;
          font-size: 16px;
          user-select: none;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          transition: fill 0.3s ease;
        }
        .timezone-label {
          font-weight: 700;
          font-size: 1.3rem;
          margin-top: 0.5rem;
          color: #222222;
          letter-spacing: 0.02em;
          user-select: none;
        }
        .time-label {
          font-size: 1.1rem;
          color: #d32f2f;
          margin-top: 0.3rem;
          font-weight: bold;
          font-family: 'Courier New', Courier, monospace;
          user-select: none;
          letter-spacing: 0.03em;
        }
        .date-label {
          font-size: 1rem;
          color: #666666;
          margin-top: 0.25rem;
          font-style: italic;
          user-select: none;
          letter-spacing: 0.02em;
        }
      `}</style>
    </div>
  );
};

const WorldClocks = ({
  width = 150,
  height = 150,
  containerHeight = 280,
} = {}) => {
  const cities = [
    { label: "Dallas Central", tz: "America/Chicago" },
    { label: "New York", tz: "America/New_York" },
    { label: "London", tz: "Europe/London" },
    { label: "Tokyo", tz: "Asia/Tokyo" },
  ];

  return (
    <>
      <div className="clocks-wrapper">
        {cities?.map(({ label, tz }) => (
          <AnalogClock
            key={tz}
            timezone={tz}
            label={label}
            width={width}
            height={height}
            containerHeight={containerHeight}
          />
        ))}
      </div>

      <style>{`
        .clocks-wrapper {
          display: flex;
          justify-content: center;
          gap: 2.5rem;
          flex-wrap: wrap;
          padding: 2.5rem;
          background-color: #f0f4f8;
          height: auto;
          align-items: center;

        }
      `}</style>
    </>
  );
};

export default WorldClocks;
